import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, BedDouble, Maximize } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner, EmptyState } from "@/components/shared";

const TYPES = ["Apartment", "Villa", "Townhouse", "Penthouse"];

export default function PropertiesPage() {
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState(sp.get("q") || "");
  const type = sp.get("property_type") || "";

  const load = useCallback(() => {
    setData(null);
    const params = new URLSearchParams();
    if (sp.get("q")) params.set("q", sp.get("q"));
    if (type) params.set("property_type", type);
    api.get(`/properties?${params.toString()}`).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, [sp, type]);
  useEffect(() => { load(); }, [load]);

  const update = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n); };

  return (
    <div className="propora-container py-10" data-testid="properties-page">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#007F83] mb-2">Discover</p>
      <h1 className="font-display font-extrabold text-4xl text-[#0B1733]">Properties & projects</h1>
      <p className="text-[#4A5568] mt-2 max-w-2xl">Explore illustrative property and project listings across Dubai. All listings shown are clearly-marked example content.</p>

      <div className="mt-6 flex flex-col md:flex-row gap-3">
        <form onSubmit={(e) => { e.preventDefault(); update("q", q); }} className="flex-1 flex gap-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or area" data-testid="property-search-input" className="bg-white h-11" />
          <Button type="submit" className="rounded-xl bg-[#007F83] hover:bg-[#006B70] text-white px-6 h-11" data-testid="property-search-btn">Search</Button>
        </form>
        <Select value={type || "all"} onValueChange={(v) => update("property_type", v === "all" ? "" : v)}>
          <SelectTrigger className="md:w-48 h-11 bg-white" data-testid="property-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6">
        {!data ? <Spinner /> : data.items.length === 0 ? <EmptyState title="No properties found" subtitle="Try a different search or type." /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.items.map((p) => (
              <Link key={p.id} to={`/properties/${p.slug}`} data-testid={`property-${p.slug}`} className="bg-white rounded-2xl border border-[#E3E8E8] overflow-hidden card-lift">
                <div className="h-52 bg-[#E8F5F1]"><img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" /></div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-[#007F83] bg-[#E8F5F1] px-2 py-0.5 rounded">{p.status}</span>
                    <span className="text-[11px] text-[#4A5568]">{p.property_type}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg text-[#0B1733]">{p.name}</h3>
                  <p className="text-sm text-[#4A5568] inline-flex items-center gap-1 mt-1"><MapPin size={13} /> {p.location}</p>
                  <div className="mt-3 flex items-center gap-4 text-xs text-[#4A5568]">
                    {p.bedrooms > 0 && <span className="inline-flex items-center gap-1"><BedDouble size={13} /> {p.bedrooms} BR</span>}
                    {p.area_sqft && <span className="inline-flex items-center gap-1"><Maximize size={13} /> {p.area_sqft} sqft</span>}
                  </div>
                  <p className="mt-3 font-display font-extrabold text-[#0B1733]">{p.asking_price}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
